import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Loader2, Mail, CheckCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { LanguageSelector } from '../../components/ui/language-selector';
import { authLogger } from '@/lib/logging';
import {
  changeLanguage,
  initializeLanguage,
  getLanguageFromURL,
  type SupportedLanguage
} from '../../lib/language-service';

export default function RequestPasswordResetPage() {
  const { i18n, t } = useTranslation();
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [languageLoading, setLanguageLoading] = useState(false);

  // Initialize language on component mount
  useEffect(() => {
    // Check for language in URL parameter first
    const urlLanguage = getLanguageFromURL();
    if (urlLanguage) {
      // If URL has language, use it
      changeLanguage(urlLanguage, false);
    } else {
      // Otherwise initialize with cookie/browser language
      initializeLanguage();
    }
    
    if (import.meta.env['MODE'] === 'development') {
      // console.warn('🔍 Password Reset Page Initialized with language:', i18n.language);
    }
  }, []);

  const handleLanguageChange = async (language: SupportedLanguage) => {
    if (languageLoading) return;
    
    setLanguageLoading(true);
    
    try {
      // Don't save to database on password reset page (user may not be authenticated)
      // Just update cookie and localStorage
      await changeLanguage(language, false);
      
      // Force a re-render by ensuring i18n knows about the change
      if (i18n.language !== language) {
        await i18n.changeLanguage(language);
      }
      
      // console.warn(`[password-reset] Language changed to: ${language}`);
    } catch (_error) {
      authLogger.error('Failed operation:', _error);
    } finally {
      setLanguageLoading(false);
    }
  };

  const handlePasswordResetRequest = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setMessage(null);
    setLoading(true);

    // Get user's current language for the password reset email
    const userLocale = i18n.language || navigator.language.split('-')[0] || 'en';
    
    // Include language in password reset URL to maintain language on completion
    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.set('lng', userLocale);

    // Development-only logging (production-safe)
    if (import.meta.env['MODE'] === 'development') {
      // console.warn('🔄 Custom Password Reset Request Starting');
      // console.warn('🌍 User locale detected:', userLocale);
    }

    try {
      // Call our custom password reset edge function
      const response = await fetch(`${import.meta.env['VITE_PUBLIC_SUPABASE_URL']}/functions/v1/send-custom-password-reset`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env['VITE_PUBLIC_SUPABASE_ANON_KEY']}`
        },
        body: JSON.stringify({
          email: email,
          locale: userLocale
        })
      });

      const data = await response.json();

      // Development-only detailed logging
      if (import.meta.env['MODE'] === 'development') {
        // console.warn('📧 Custom Password Reset Response:', {
        //   success: response.ok,
        //   status: response.status,
        //   message: data.message,
        //   error: data.error,
        // });
      }

      setLoading(false);

      if (!response.ok) {
        if (import.meta.env['MODE'] === 'development') {
          authLogger.error('Failed operation:', data.error);
        }
        setError(data.error || t('auth.passwordReset.errorOccurred'));
      } else {
        setIsSuccess(true);
        setMessage(data.message || 'If an account exists for this email, a password reset link has been sent.');
      }
    } catch (unexpectedError) {
      if (import.meta.env['MODE'] === 'development') {
        authLogger.error('💥 Unexpected Error in Custom Password Reset:', unexpectedError);
      }
      setLoading(false);
      setError(t('auth.passwordReset.unexpectedError'));
    }
  };

  return (
    <div 
      className='flex items-center justify-center min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-4 transition-colors duration-500' 
      style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        minHeight: '100vh',
        width: '100%',
        position: 'fixed',
        top: 0,
        left: 0
      }}
    >
      <div className='w-full max-w-md'>
        <Card className='shadow-xl border-0 bg-white/80 backdrop-blur-sm transition-all duration-300 hover:shadow-2xl'>
          <CardHeader className='text-center pb-8'>
            <div className='flex justify-end mb-4'>
              <LanguageSelector
                value={i18n.language as SupportedLanguage}
                onLanguageChange={handleLanguageChange}
                disabled={languageLoading || loading}
                variant="select"
                size="sm"
                showIcon={true}
                showLabel={false}
              />
            </div>
            <CardTitle className='text-3xl font-bold text-slate-900'>
              {t('auth.passwordReset.title')}
            </CardTitle>
            <CardDescription className='text-slate-600 mt-2'>
              {isSuccess
                ? t('auth.passwordReset.checkEmail')
                : t('auth.passwordReset.enterEmail')}
            </CardDescription>
          </CardHeader>
          <CardContent className='pb-8'>
            {!isSuccess ? (
              <form onSubmit={handlePasswordResetRequest} className='space-y-6' data-testid="reset-form">
                <div className='space-y-2'>
                  <Label htmlFor='email' className='text-sm font-medium text-slate-700'>
                    {t('auth.login.emailLabel')}
                  </Label>
                  <Input
                    id='email'
                    type='email'
                    placeholder='you@example.com'
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={loading}
                    data-testid="email-input"
                    className='h-12 transition-all duration-200 focus:ring-2 focus:ring-primary/20 border-slate-200 hover:border-primary/40'
                  />
                </div>
                {error && (
                  <div className='p-3 bg-red-50 border border-red-200 rounded-lg' data-testid="error-message">
                    <p className='text-sm text-red-700'>{error}</p>
                  </div>
                )}
                <Button 
                  type='submit' 
                  data-testid="reset-button"
                  className='w-full h-12 text-base font-medium transition-all duration-200 hover:shadow-lg hover:scale-[1.02] active:scale-[0.98]' 
                  disabled={loading}
                >
                  {loading ? (
                    <div className='flex items-center justify-center'>
                      <Loader2 className='mr-2 h-5 w-5 animate-spin' />
{t('auth.passwordReset.sendingResetLink')}
                    </div>
                  ) : (
                    <div className='flex items-center justify-center'>
                      <Mail className='mr-2 h-5 w-5' />
{t('auth.passwordReset.sendResetLink')}
                    </div>
                  )}
                </Button>
              </form>
            ) : (
              <div className='space-y-6'>
                <div className='p-4 bg-green-50 border border-green-200 rounded-lg' data-testid="success-message">
                  <div className='flex items-center'>
                    <CheckCircle className='h-6 w-6 text-green-600 mr-3' />
                    <div>
                      <p className='text-sm font-medium text-green-800'>{t('auth.passwordReset.resetLinkSent')}</p>
                      <p className='text-sm text-green-700 mt-1'>{message}</p>
                    </div>
                  </div>
                </div>
                <div className='text-center text-sm text-slate-600'>
                  <p>{t('auth.passwordReset.didntReceiveEmail')}</p>
                  <button
                    onClick={() => {
                      setIsSuccess(false);
                      setMessage(null);
                      setError(null);
                    }}
                    className='font-medium text-primary hover:text-primary/80 transition-colors duration-200'
                  >
{t('auth.passwordReset.tryAgain')}
                  </button>
                </div>
              </div>
            )}
            <div className='mt-8 space-y-4 text-center'>
              <div className='relative'>
                <div className='absolute inset-0 flex items-center'>
                  <div className='w-full border-t border-slate-200' />
                </div>
                <div className='relative flex justify-center text-sm'>
                  <span className='px-4 bg-white text-slate-500'>{t('auth.passwordReset.rememberPassword')}</span>
                </div>
              </div>
              <p className='text-sm text-slate-600'>
                <Link 
                  to='/login' 
                  className='font-medium text-primary hover:text-primary/80 transition-colors duration-200'
                >
{t('auth.passwordReset.backToLogin')}
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}